/* eslint-disable no-param-reassign */
/* eslint-disable no-unused-vars */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import companyDataService from "services/company";

// --- Initial State ---
const initialState = {
  companies: {
    items: [],
    totalCount: 0,
  },
  companiesForSignature: {
    items: [],
    totalCount: 0,
  },
  managedCompanies: {
    items: [],
    totalCount: 0,
  },
  getCompanies: {
    status: "idle",
    error: false,
  },
  getCompaniesForSignature: {
    status: "idle",
    error: false,
  },
  getManagedCompanies: {
    status: "idle",
    error: false,
  },
  getTransactionCounts: {
    status: "idle",
    error: false,
  },
  transactionCounts: {
    reviewTransaction: 0,
    signatureTransaction: 0,
    correctionTransaction: 0,
  },
};

// --- Async Thunks ---
export const getCompaniesAsync = createAsyncThunk(
  "companies/getCompaniesAsync",
  async (queryParams, thunkAPI) => {
    try {
      const res = await companyDataService.getAll(queryParams);
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data || error.message || "Unknown error"
      );
    }
  }
);

export const getCompaniesForSignatureAsync = createAsyncThunk(
  "companies/getCompaniesForSignatureAsync",
  async (queryParams, thunkAPI) => {
    try {
      const res = await companyDataService.getAll(queryParams);
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data || error.message || "Unknown error"
      );
    }
  }
);

export const deleteCompany = createAsyncThunk(
  "companies/deleteCompany",
  async ({ companyId, queryParams = {} }, thunkAPI) => {
    try {
      await companyDataService.remove(companyId, queryParams);
      return { companyId };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data || error.message || "Unknown error"
      );
    }
  }
);

export const getManagedCompaniesAsync = createAsyncThunk(
  "companies/getManagedCompaniesAsync",
  async (queryParams, thunkAPI) => {
    try {
      const res = await companyDataService.getManagedCompanies(queryParams);
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data || error.message || "Unknown error"
      );
    }
  }
);

export const getTransactionCountsByTypesAsync = createAsyncThunk(
  "companies/getTransactionCountsByTypesAsync",
  async (_, thunkAPI) => {
    try {
      const res = await companyDataService.getTransactionCountsByTypes();
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data || error.message || "Unknown error"
      );
    }
  }
);

// --- Slice ---
const companySlice = createSlice({
  name: "companies",
  initialState,
  reducers: {
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCompaniesAsync.pending, (state) => {
        state.getCompanies.status = "loading";
        state.getCompanies.error = false;
      })
      .addCase(getCompaniesAsync.fulfilled, (state, action) => {
        state.getCompanies.status = "idle";
        state.getCompanies.error = false;
        state.companies = action.payload;
      })
      .addCase(getCompaniesAsync.rejected, (state, action) => {
        state.getCompanies.status = "failed";
        state.getCompanies.error =
          action?.payload || "Failed to fetch companies";
      })
      .addCase(getCompaniesForSignatureAsync.pending, (state) => {
        state.getCompaniesForSignature.status = "loading";
        state.getCompaniesForSignature.error = false;
      })
      .addCase(getCompaniesForSignatureAsync.fulfilled, (state, action) => {
        state.getCompaniesForSignature.status = "idle";
        state.getCompaniesForSignature.error = false;
        state.companiesForSignature = action.payload;
      })
      .addCase(getCompaniesForSignatureAsync.rejected, (state, action) => {
        state.getCompaniesForSignature.status = "failed";
        state.getCompaniesForSignature.error =
          action?.payload || "Failed to fetch companies for signature";
      })
      .addCase(getManagedCompaniesAsync.pending, (state) => {
        state.getManagedCompanies.status = "loading";
        state.getManagedCompanies.error = false;
      })
      .addCase(getManagedCompaniesAsync.fulfilled, (state, action) => {
        state.getManagedCompanies.status = "idle";
        state.getManagedCompanies.error = false;
        state.managedCompanies = action.payload.managedCompanies;
      })
      .addCase(getManagedCompaniesAsync.rejected, (state, action) => {
        state.getManagedCompanies.status = "failed";
        state.getManagedCompanies.error =
          action?.payload || "Failed to fetch managed companies";
      })
      .addCase(getTransactionCountsByTypesAsync.pending, (state) => {
        state.getTransactionCounts.status = "loading";
        state.getTransactionCounts.error = false;
      })
      .addCase(getTransactionCountsByTypesAsync.fulfilled, (state, action) => {
        state.getTransactionCounts.status = "idle";
        state.getTransactionCounts.error = false;
        state.transactionCounts = action.payload;
      })
      .addCase(getTransactionCountsByTypesAsync.rejected, (state, action) => {
        state.getTransactionCounts.status = "failed";
        state.getTransactionCounts.error =
          action?.payload || "Failed to fetch transaction counts";
      })
      .addCase(deleteCompany.fulfilled, (state, { payload }) => {
        const index = state.companies.items.findIndex(
          (company) => payload.companyId === company._id
        );
        if (index > -1) {
          state.companies.items.splice(index, 1);
          state.companies.totalCount -= 1;
        }
      });
  },
});

// --- Exports ---
export const { reset } = companySlice.actions;
export default companySlice.reducer;
